import type { CuboidDataT, OutputAnnotationT, PDFOutputLabelledDataT } from '../../labelling/types';
import { getCanvasContextWithImage } from './get-canvas-context';
export const scalePDFOutputLabelledData = async <T extends PDFOutputLabelledDataT,>(output: T, scale: number): Promise<T> => {
  const frames = output.frames;
  const updatedFrames = Promise.all(frames.map(async frame => {
    const annotations = await Promise.all(frame.annotations.map(annotation => scaleOutputAnnotation(annotation, scale)));
    return {
      ...frame,
      annotations
    };
  }));
  return {
    ...output,
    frames: await updatedFrames
  };
};
const scaleOutputAnnotation = async (annotation: OutputAnnotationT, scale: number): Promise<OutputAnnotationT> => {
  switch (annotation.annotationType) {
    case 'RECTANGLE':
      return {
        ...annotation,
        data: {
          ...annotation.data,
          x: annotation.data.x * scale,
          y: annotation.data.y * scale,
          width: annotation.data.width * scale,
          height: annotation.data.height * scale
        }
      };
    case 'POLYGON':
      return {
        ...annotation,
        data: {
          ...annotation.data,
          points: annotation.data.points.map<[number, number]>(point => [point[0] * scale, point[1] * scale])
        }
      };
    case 'ELLIPSE':
      return {
        ...annotation,
        data: {
          ...annotation.data,
          centerX: annotation.data.centerX * scale,
          centerY: annotation.data.centerY * scale,
          radiusX: annotation.data.radiusX * scale,
          radiusY: annotation.data.radiusY * scale
        }
      };
    case 'CUBOID':
      return {
        ...annotation,
        data: {
          ...annotation.data,
          points: ([[annotation.data.points[0][0] * scale, annotation.data.points[0][1] * scale], [annotation.data.points[1][0] * scale, annotation.data.points[1][1] * scale], [annotation.data.points[2][0] * scale, annotation.data.points[2][1] * scale], [annotation.data.points[3][0] * scale, annotation.data.points[3][1] * scale], [annotation.data.points[4][0] * scale, annotation.data.points[4][1] * scale], [annotation.data.points[5][0] * scale, annotation.data.points[5][1] * scale], [annotation.data.points[6][0] * scale, annotation.data.points[6][1] * scale], [annotation.data.points[7][0] * scale, annotation.data.points[7][1] * scale]] as CuboidDataT['points'])
        }
      };
    case 'POLYLINE':
      return {
        ...annotation,
        data: {
          ...annotation.data,
          points: annotation.data.points.map<[number, number]>(point => [point[0] * scale, point[1] * scale])
        }
      };
    case 'KEY_POINT':
      return {
        ...annotation,
        data: {
          ...annotation.data,
          x: annotation.data.x * scale,
          y: annotation.data.y * scale
        }
      };
    case 'FRAME_CLASSIFICATION':
      return annotation;
    case 'SKELETON':
      return {
        ...annotation,
        data: {
          ...annotation.data,
          nodes: annotation.data.nodes.map(node => ({
            ...node,
            point: [node.point[0] * scale, node.point[1] * scale]
          }))
        }
      };
    case 'SEGMENTATION':
      if (!annotation.data.base64String) {
        return annotation;
      }
      const canvasContext = await getCanvasContextWithImage(annotation.data.base64String, scale);
      if (!canvasContext) {
        return annotation;
      }
      const {
        canvas,
        cleanedUp
      } = canvasContext;
      const base64String = canvas.toDataURL('image/png');
      cleanedUp();
      return {
        ...annotation,
        data: {
          ...annotation.data,
          base64String
        }
      };
  }
};